import { C_Statistic_Calc_Status } from '../../../graphql/generatedModel';

/**
 * How many times a statistic job is polled (one poll per second after an initial 1.3 s) before
 * the client stops waiting. The server-side calculation itself finishes in seconds (the worker's
 * slowest run in two weeks of production was 29 s), so a job that is still SCS_LOADING here has
 * effectively been lost, not merely delayed.
 */
export const STATISTIC_MAX_POLLING_ATTEMPTS = 50;

export class StatisticJobError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StatisticJobError';
  }
}

interface IStatisticJobResult<D> {
  status: C_Statistic_Calc_Status;
  data?: D | null;
}

/**
 * Returns the payload of a finished statistic job, or throws a `StatisticJobError` that names
 * the actual state the job was left in.
 *
 * Both statistic services used to reach into `res.data` straight after polling, whatever the
 * status. A job that was still running after the last poll, or that the worker had flagged as
 * SCS_ERROR, therefore crashed with "Cannot destructure property 'items' from null or undefined
 * value" (WA-3887, Sentry WAWIASSIST-ET / WAWIASSIST-24V) - a message that neither told the user
 * anything nor let Sentry group the two causes apart. The messages below deliberately carry no
 * job id, so each cause stays one Sentry group.
 */
export const takeFinishedStatisticData = <D>(
  res: IStatisticJobResult<D> | null | undefined,
  jobKind: 'Order statistic' | 'Production statistic',
): D => {
  if (!res) {
    throw new StatisticJobError(`${jobKind} calculation returned no status`);
  }
  if (res.status === C_Statistic_Calc_Status.SCS_LOADING) {
    throw new StatisticJobError(
      `${jobKind} calculation was still running after ${STATISTIC_MAX_POLLING_ATTEMPTS} polls`,
    );
  }
  if (res.status === C_Statistic_Calc_Status.SCS_ERROR) {
    throw new StatisticJobError(`${jobKind} calculation failed on the server`);
  }
  if (!res.data) {
    throw new StatisticJobError(`${jobKind} calculation finished without data`);
  }
  return res.data;
};
